import React, { useCallback, useEffect, useMemo, useState } from 'react';
import ReactModal from 'react-modal';
import request from '../../../../helpers/request';
import endpoints from '../../../../helpers/endpoints';
import syncRowHeights from '../../../../helpers/syncRowHeights';
import { GenericTable } from '../GenericTable';
import { PostcodeSearchModal } from '../modals/PostcodeSearchModal';

export const MeterAddress = ({ translations, summaryData: data, selectedMatches, setSelectedMatches, prefix, disabledByUserType, userInput, setUserInput }) => {
  const [loading, setLoading] = useState(false);
  const [addresses, setAddresses] = useState();
  const [postcodeModal, setPostcodeModal] = useState(false);
  const [customPostcode, setCustomPostcode] = useState('');
  const [clearMatchModal, setClearMatchModal] = useState(false);
  const [index, setIndex] = useState(0);
  const [source, setSource] = useState("internal")

  const cycleIndex = useCallback((change = 0) => {
    if (change === 0) return
    if (index + change <= -1) return
    if (index + change >= addresses?.length) return
    setIndex(index + change)
  }, [index, addresses])

  const getData = (customInput) => {
    if (!customInput && !customPostcode && !data.CF_Meter_Address_Postcode && data.CMOS_Postcode) {
        setLoading(false);
        return;
    }
    setAddresses()
    setIndex(0)
    setLoading(true)
    request(true).get(endpoints[`${prefix}_GET_METER`], {
      doesCancel: true,
      params: {
        postcode: customInput || data.CF_Meter_Address_Postcode || data.CMOS_Postcode
      }
    })
    .then(e => {
      const _data = e.data;
      setAddresses(_data)
      var displayFirst = -1
      if (data.Meter_ID) {
        displayFirst = _data.findIndex(_ => `${_.Manufacturer_Meter_Serial_Number}_${_.Meter_Manufacturer}` === data.Meter_ID) 
        setIndex(displayFirst !== -1 ? displayFirst : 0);
        setSelectedMatches((prevMatches) => {
          return {
            ...prevMatches,
            meter: typeof data.Meter_ID === "number" ? data.Meter_ID.toString() : data.Meter_ID 
          };
        });
      }
      setUserInput(_data[displayFirst !== -1 ? displayFirst : 0])
      setSource("external")
      setLoading(false)
    })
    .catch(error => { 
      setAddresses(); 
      setLoading(false)
      console.log(error) 
      window.alert('Could not get Meter data.'); 
    })
  };

  useEffect(() => {
    if (syncRowHeights) {
      syncRowHeights()
    }
  }, [addresses, index])

  useEffect(() => {
    setAddresses()
    setIndex(-1)
    setUserInput({})
    if (data.Core_SPID) {
      setIndex(-1)
      setCustomPostcode('')
      setLoading(false)
      setUserInput(data ?? {})
      setSource("internal")
    } else {
      setLoading(false)
    }
  }, [data.Core_SPID])

  const restorePostcode = useCallback(() => {
    setCustomPostcode(''); 
    setLoading(false)
    setAddresses()
    setUserInput(data ?? {})
    setSource("internal")
  }, [])

  const handleInputChange = useCallback((newInput) => {
    if (!newInput) return
    setUserInput(prevUserInput => ({
        ...prevUserInput,
        ...newInput,
    }));
  }, [setUserInput]);

  function formatAddress(fields) {
    const formattedAddress = {};
  
    const addressLines = [
      'CF_Meter_Address_Address_Line_1',
      'CF_Meter_Address_Address_Line_2',
      'CF_Meter_Address_Address_Line_3',
      'CF_Meter_Address_Address_Line_4',
      'CF_Meter_Address_Address_Line_5',
    ];
  
    const components = fields.map(field => selectedMatches.abp?.[field]).filter(value => value && value.trim() !== '');
  
    components.forEach((component, index) => {
      if (index < addressLines.length) {
        formattedAddress[addressLines[index]] = component;
      }
    });
  
    addressLines.forEach(line => {
      if (!formattedAddress[line]) {
        formattedAddress[line] = '';
      }
    });
  
    return formattedAddress;
  }

  const handleUpdateOnUprn = () => {
    if (!selectedMatches.abp) return
    setAddresses()
    setIndex()
    restorePostcode()
    const choice = selectedMatches.abp
    var SAO = '';
    if (!choice.SUB_BUILDING && !choice.BUILDING_NAME && !choice.BUILDING_NUMBER) {
      SAO = choice.ORGANISATION
    }
    setUserInput({
      ...userInput,
      CF_UPRN: choice.UPRN,
      CF_UPRN_Reason_Code: '',
      CF_Meter_GISX: choice.EASTING ?? '',
      CF_Meter_GISY: choice.NORTHING ?? '',
      CF_Meter_Address_Secondary_Addressable_Object: SAO || choice.SUB_BUILDING || '',
      CF_Meter_Address_Primary_Addressable_Object: choice.STREET_NAME ? (choice.BUILDING_NAME || choice.BUILDING_NUMBER || '') : '',
      CF_Meter_Address_Address_Line_1: choice.STREET_NAME || choice.BUILDING_NAME || choice.BUILDING_NUMBER || '',
      CF_Meter_Address_Address_Line_2: choice.LOCALITY ?? '',
      CF_Meter_Address_Address_Line_3: choice.TOWN_NAME ?? '',
      CF_Meter_Address_Address_Line_4: choice.POST_TOWN ?? '',
      CF_Meter_Address_Address_Line_5: choice.ISLAND ?? '',
      CF_Meter_Address_Postcode: choice.POSTCODE ?? ''
    })
    syncRowHeights()
    setSource('internal')
  }

  useEffect(() => {
    if (source === "external") {
      // const fields = [
      //   'Address_Address_Line_1',
      //   'Address_Address_Line_2',
      //   'Address_Address_Line_3',
      //   'Address_Address_Line_4',
      //   'Address_Address_Line_5',
      //   'Address_Postcode'
      // ]
      // const formattedAddress = formatAddress(fields)
      setUserInput({
        ...userInput,
        CF_UPRN: addresses[index]?.UPRN ?? '',
        CF_GISX: addresses[index]?.GISX ?? '',
        CF_GISY: addresses[index]?.GISY ?? '',
        CF_Meter_Address_Address_Line_1: addresses[index]?.Address_Address_Line_1 ?? '',
        CF_Meter_Address_Address_Line_2: addresses[index]?.Address_Address_Line_2 ?? '',
        CF_Meter_Address_Address_Line_3: addresses[index]?.Address_Address_Line_3 ?? '',
        CF_Meter_Address_Address_Line_4: addresses[index]?.Address_Address_Line_4 ?? '',
        CF_Meter_Address_Address_Line_5: addresses[index]?.Address_Address_Line_5 ?? '',
        CF_Meter_Address_Postcode: addresses[index]?.Address_Postcode ?? ''
      })
    }
  }, [index, addresses])

  const rows = useMemo(() => {
    var matchEnabled = false

    if (!loading) {
      const fields = [
        'SUB_BUILDING',
        'BUILDING_NAME',
        'BUILDING_NUMBER',
        'STREET_NAME',
        'TOWN_NAME',
        'POST_TOWN',
      ];
      const formattedAddress = formatAddress(fields)
      formattedAddress.CF_GISX = selectedMatches?.abp?.EASTING
      formattedAddress.CF_GISY = selectedMatches?.abp?.NORTHING
      formattedAddress.CF_Meter_Address_Postcode = selectedMatches?.abp?.POSTCODE

      const fieldMappings = {
        CF_GISX: 'CF_GISX',
        CF_GISY: 'CF_GISY',
        CF_Meter_Address_Secondary_Addressable_Object: source === "external" ? 'Address_Secondary_Addressable_Object' : 'CF_Meter_Address_Secondary_Addressable_Object', 
        CF_Meter_Address_Primary_Addressable_Object: source === "external" ? 'Address_Primary_Addressable_Object' : 'CF_Meter_Address_Primary_Addressable_Object',
        CF_Meter_Address_Address_Line_1: source === "external" ? 'Address_Address_Line_1' : 'CF_Meter_Address_Address_Line_1',
        CF_Meter_Address_Address_Line_2: source === "external" ? 'Address_Address_Line_2' : 'CF_Meter_Address_Address_Line_2',
        CF_Meter_Address_Address_Line_3: source === "external" ? 'Address_Address_Line_3' : 'CF_Meter_Address_Address_Line_3',
        CF_Meter_Address_Address_Line_4: source === "external" ? 'Address_Address_Line_4' : 'CF_Meter_Address_Address_Line_4',
        CF_Meter_Address_Address_Line_5: source === "external" ? 'Address_Address_Line_5' : 'CF_Meter_Address_Address_Line_5',
        CF_Meter_Address_Postcode: source === "external" ? 'Address_Postcode' : 'CF_Meter_Address_Postcode'
      };
      if (userInput) {
        matchEnabled = selectedMatches?.abp && Object.keys(fieldMappings).some(
          key => userInput[key] != formattedAddress[fieldMappings[key]]
        );
      }
    }

    return [
      [translations['Outreader_Protocol'], 'Outreader_Protocol'],
      [translations['Meter_Location_Free_Desc'], addresses ? 'Meter_Outreader_Location_Free_Desc' : 'CF_Meter_Location_Free_Desc'],
      [translations['Meter_Location_Code'], 'Meter_Location_Code'],
      [translations['GISX'], {hasInput: true, column: 'CF_Meter_GISX', value: userInput?.['CF_Meter_GISX'], disabled: disabledByUserType}],
      [translations['GISY'], {hasInput: true, column: 'CF_Meter_GISY', value: userInput?.['CF_Meter_GISY'], disabled: disabledByUserType}],
      [translations['Occupier'], ''],
      [translations['CL_Secondary_Addressable_Object'], addresses ? 'Address_Secondary_Addressable_Object' : 'CF_Meter_Address_Secondary_Addressable_Object'],
      [translations['CL_Primary_Addressable_Object'], addresses ? 'Address_Primary_Addressable_Object' : "CF_Meter_Address_Primary_Addressable_Object"],
      [translations['CL_Address_Line_1'], addresses ? 'Address_Address_Line_1' : 'CF_Meter_Address_Address_Line_1'],
      [translations['CL_Address_Line_2'], addresses ? 'Address_Address_Line_2' : 'CF_Meter_Address_Address_Line_2'],
      [translations['CL_Address_Line_3'], addresses ? 'Address_Address_Line_3' : 'CF_Meter_Address_Address_Line_3'],
      [translations['CL_Address_Line_4'], addresses ? 'Address_Address_Line_4' : 'CF_Meter_Address_Address_Line_4'],
      [translations['CL_Address_Line_5'], addresses ? 'Address_Address_Line_5' : 'CF_Meter_Address_Address_Line_5'],
      [translations['CL_Postcode'], addresses ? 'Address_Postcode' : "CF_Meter_Address_Postcode"],
      [
        () => <button disabled={disabledByUserType} className={`button compact smaller-text ${matchEnabled && !disabledByUserType ? ' background-primary colour-white' : ''}`} onClick={() => matchEnabled && handleUpdateOnUprn()}>Update on UPRN</button>,
      ],
    ]
  }, [translations, window.reloadSummaryData, index, selectedMatches, addresses, userInput, setUserInput, source])

  return [
    <GenericTable title={translations['Meter_Address_Header']} rows={rows} data={addresses ?? userInput} loading={loading} restorePostcode={restorePostcode} index={index} cycleIndex={cycleIndex} customPostcode={customPostcode} searchEnabled={true} setPostcodeModal={setPostcodeModal} handleInputChange={handleInputChange} userInput={userInput} />,
    <PostcodeSearchModal postcodeModal={postcodeModal} setPostcodeModal={setPostcodeModal} customPostcode={customPostcode} setCustomPostcode={setCustomPostcode} setLoading={setLoading} getData={getData} />,
    <ReactModal
      isOpen={clearMatchModal}
      onRequestClose={() => setClearMatchModal(false)}
      className="card bulk-allocate"
      contentLabel="Bulk Allocate COT Alert Data"
      style={{ overlay: { backgroundColor: 'rgba(14, 14, 14, 0.55)' } }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: 10 }}>
        <h3>Clear existing match</h3>
        <p>Please confirm you wish to clear the following matched Reference for Meter on this SPID.</p>
        <ul className='spid-list'>
          <li>SPID: {data.Core_SPID}</li>
          <li>CT Reference: {data.au_reference_number}</li>
        </ul>
        <div style={{display: 'flex', flexDirection: 'row', justifyContent: 'space-evenly', alignItems: 'center'}}>
          <button className="button compact smaller-text cancel" onClick={ () => setClearMatchModal(false) }>Cancel</button>
          <button className="button compact smaller-text background-primary colour-white" onClick={() => {
            request(true).post(endpoints[`${prefix}_CLEAR_MATCH`], {
              spid: data.Core_SPID,
              matchType: 'ct'
            }).then(r => {
              window.reloadSummaryData()
              setClearMatchModal(false); 
            })
          }}
          >Confirm</button> 
        </div>
      </div>
    </ReactModal>
  ]
}